import React, { useEffect, useMemo, useState } from 'react';

export const ROUTER_TYPES = [
  { value: 'mikrotik', label: 'MikroTik' },
  { value: 'cisco', label: 'Cisco (IOS/IOS-XE)' },
  { value: 'ubiquiti', label: 'Ubiquiti (UniFi)' },
  { value: 'tp-link', label: 'TP-Link (Omada)' },
  { value: 'other', label: 'Other' },
];

const defaults = {
  type: 'mikrotik',
  name: 'OPENFI-',
  site: '',
  notes: '',
  model: '',
  serial: '',
  mac: '',
  wanIp: '',
  apiPort: 8728,
  sshPort: 22,
  location: '',
  latitude: '',
  longitude: '',
  hotspotName: 'hotspot1',
  ssid: 'Openfi Network',          // NEW: default SSID
  radiusEnabled: true,
  active: true,
  portalVisible: true,
};

const toInt = (v, d = null) => {
  if (v === '' || v === null || typeof v === 'undefined') return d;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? d : n;
};

const isIPv4 = (s) => /^(\d{1,3}\.){3}\d{1,3}$/.test(String(s || '').trim());

// ---------- helpers for MAC + name ----------
const normalizeMac = (raw = '') =>
  raw
    .toUpperCase()
    .replace(/[^0-9A-F]/g, '')           // keep hex only
    .match(/.{1,2}/g)?.join(':') || '';  // re-insert :

const computeRouterName = (mac) => (mac ? `OPENFI-${mac}` : 'OPENFI-');

export default function RouterForm({
  editingId = null,
  initialValues,
  onSubmit,
  endpointCreate = '/api/platform/webclient/new_router',
  endpointUpdate = '/api/platform/webclient/update_router',
}) {
  const [form, setForm] = useState(() => ({ ...defaults, ...(initialValues ?? {}) }));
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // {type:'success'|'error', msg}

  // Merge new initialValues when they arrive (no loop)
  useEffect(() => {
    if (initialValues) {
      const mac = normalizeMac(initialValues.mac || '');
      setForm((prev) => ({
        ...prev,
        ...initialValues,
        mac,
        // If incoming data has no ssid, keep default
        ssid: (initialValues.ssid ?? prev.ssid ?? 'Openfi Network'),
        name: computeRouterName(mac),
      }));
    }
  }, [initialValues]);

  // Keep name derived from MAC at all times
  useEffect(() => {
    const mac = normalizeMac(form.mac || '');
    if (form.name !== computeRouterName(mac)) {
      setForm((p) => ({ ...p, mac, name: computeRouterName(mac) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.mac]);

  const isMikroTik = useMemo(() => form.type === 'mikrotik', [form.type]);

  const onChange = (e) => {
    const { name, type, value, checked } = e.target;

    // Name is read-only in UI; ignore user edits if any slip through.
    if (name === 'name') return;

    // Normalize MAC as user types
    if (name === 'mac') {
      const mac = normalizeMac(value);
      setForm((p) => ({ ...p, mac, name: computeRouterName(mac) }));
      return;
    }

    setForm((p) => ({ ...p, [name]: type === 'checkbox' ? !!checked : value }));
  };

  const resetForm = () => {
    setForm({ ...defaults });
    setStatus(null);
  };

  const validate = () => {
    if (!form.type) return 'Router type is required.';
    // Name is auto-generated; don't require manual entry.
    if (!String(form.site || '').trim()) return 'Site is required.';
    const mac = normalizeMac(form.mac);
    if (!mac) return 'MAC address is required.';
    if (form.wanIp && !isIPv4(form.wanIp)) return 'WAN IP looks invalid.';
    if (!(Number(form.sshPort) > 0)) return 'SSH port must be > 0.';
    if (isMikroTik) {
      if (!(Number(form.apiPort) > 0)) return 'API port must be > 0 (MikroTik).';
      if (!String(form.hotspotName || '').trim()) return 'Hotspot/Profile name is required (MikroTik).';
    }
    // SSID not required (it has a default), but you can enforce if you want:
    // if (!String(form.ssid || '').trim()) return 'SSID is required.';
    return null;
  };

  const buildPayload = () => {
    const mac = normalizeMac(form.mac);
    const forcedName = computeRouterName(mac);

    return {
      name: forcedName,                                // force server-side
      router_type: String(form.type || '').trim(),
      site: String(form.site || '').trim(),
      description: String(form.notes || '').trim(),
      model: form.model || null,
      serial: form.serial || null,
      mac: mac || null,
      wan_ip: form.wanIp || null,
      ssh_port: toInt(form.sshPort, 22),
      api_port: form.type === 'mikrotik' ? toInt(form.apiPort, 8728) : null,
      hotspot_name: form.type === 'mikrotik' ? (form.hotspotName || null) : null,
      SSID: String(form.ssid || 'Openfi Network').trim(),   // NEW: send SSID
      location: form.location || null,
      latitude: form.latitude || null,
      longitude: form.longitude || null,
      radius_enabled: form.radiusEnabled ? 1 : 0,
      is_visible: form.portalVisible ? 1 : 0,
      status: form.active ? 'active' : 'inactive',
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    const err = validate();
    if (err) return setStatus({ type: 'error', msg: err });

    const payload = buildPayload();
    try {
      setSaving(true);

      if (typeof onSubmit === 'function') {
        const res = await onSubmit(payload, { mode: editingId ? 'update' : 'create', id: editingId });
        if (res?.ok) {
          setStatus({ type: 'success', msg: res.message || (editingId ? 'Router updated.' : 'Router registered.') });
          if (!editingId) resetForm();
        } else {
          setStatus({ type: 'error', msg: res?.message || 'Operation failed.' });
        }
        return;
      }

      // Fallback direct API calls
      if (editingId) {
        const r = await fetch(endpointUpdate, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: editingId, changes: buildPayload() }),
        });
        const j = await r.json().catch(() => ({}));
        if (r.ok && j?.success !== false) {
          setStatus({ type: 'success', msg: j?.message || 'Router updated.' });
        } else {
          setStatus({ type: 'error', msg: j?.message || 'Failed to update router.' });
        }
      } else {
        const r = await fetch(endpointCreate, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const j = await r.json().catch(() => ({}));
        if (r.ok && j?.success !== false) {
          setStatus({ type: 'success', msg: j?.message || 'Router registered.' });
          resetForm();
        } else {
          setStatus({ type: 'error', msg: j?.message || 'Failed to register router.' });
        }
      }
    } catch (ex) {
      setStatus({ type: 'error', msg: ex?.message || 'Unexpected error.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="row">
      <div className="col-lg-8">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="card-title mb-1">{editingId ? 'Edit Router' : 'Register Router'}</h5>
                <p className="text-muted mb-0">Pick vendor type to see the right fields.</p>
              </div>
              {status?.msg && (
                <div className={`alert mb-0 ${status.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
                  {status.msg}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group col-md-4">
                  <label htmlFor="type">Router Type</label>
                  <select id="type" name="type" className="form-control" value={form.type} onChange={onChange}>
                    {ROUTER_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group col-md-4">
                  <label htmlFor="name">Router Name</label>
                  <input
                    id="name"
                    name="name"
                    className="form-control"
                    value={form.name}
                    onChange={onChange}
                    readOnly
                    placeholder="OPENFI-<MAC>"
                  />
                  <small className="form-text text-muted">Auto-generated from MAC.</small>
                </div>
                <div className="form-group col-md-4">
                  <label htmlFor="site">Site</label>
                  <input id="site" name="site" className="form-control" value={form.site} onChange={onChange} placeholder="e.g. Main Campus" required />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-4">
                  <label htmlFor="model">Model</label>
                  <input id="model" name="model" className="form-control" value={form.model} onChange={onChange} placeholder="RB4011 / IOS-XE / UniFi…" />
                </div>
                <div className="form-group col-md-4">
                  <label htmlFor="serial">Serial</label>
                  <input id="serial" name="serial" className="form-control" value={form.serial} onChange={onChange} placeholder="Device serial" />
                </div>
                <div className="form-group col-md-4">
                  <label htmlFor="mac">MAC Address *</label>
                  <input
                    id="mac"
                    name="mac"
                    className="form-control"
                    value={form.mac}
                    onChange={onChange}
                    placeholder="64:D1:54:39:20:47"
                    required
                    inputMode="text"
                    pattern="^[0-9A-Fa-f:\-]{12,17}$"
                    title="Enter a valid MAC address"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-4">
                  <label htmlFor="wanIp">WAN IP (optional)</label>
                  <input id="wanIp" name="wanIp" className="form-control" value={form.wanIp} onChange={onChange} placeholder="41.210.x.x" />
                </div>

                {isMikroTik && (
                  <>
                    <div className="form-group col-md-4">
                      <label htmlFor="apiPort">API Port</label>
                      <input type="number" id="apiPort" name="apiPort" className="form-control" value={form.apiPort} onChange={onChange} min="1" />
                    </div>
                    <div className="form-group col-md-4">
                      <label htmlFor="hotspotName">Hotspot/Profile Name</label>
                      <input id="hotspotName" name="hotspotName" className="form-control" value={form.hotspotName} onChange={onChange} placeholder="hsprof1 / hotspot1" required />
                    </div>
                  </>
                )}

                <div className={`form-group ${isMikroTik ? 'col-md-4' : 'col-md-8'}`}>
                  <label htmlFor="sshPort">SSH Port</label>
                  <input type="number" id="sshPort" name="sshPort" className="form-control" value={form.sshPort} onChange={onChange} min="1" />
                </div>
              </div>

              {/* NEW: SSID field (applies to all types) */}
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label htmlFor="ssid">SSID</label>
                  <input
                    id="ssid"
                    name="ssid"
                    className="form-control"
                    value={form.ssid}
                    onChange={onChange}
                    placeholder="Openfi Network"
                  />
                  <small className="form-text text-muted">Default: Openfi Network</small>
                </div>
                <div className="form-group col-md-6">
                  <label htmlFor="location">Location (label)</label>
                  <input id="location" name="location" className="form-control" value={form.location} onChange={onChange} placeholder="Room / Street / Floor" />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-3">
                  <label htmlFor="latitude">Latitude (optional)</label>
                  <input id="latitude" name="latitude" className="form-control" value={form.latitude} onChange={onChange} placeholder="-0.3476" />
                </div>
                <div className="form-group col-md-3">
                  <label htmlFor="longitude">Longitude (optional)</label>
                  <input id="longitude" name="longitude" className="form-control" value={form.longitude} onChange={onChange} placeholder="32.5825" />
                </div>
              </div>

              <div className="d-flex align-items-center mb-3">
                <div className="custom-control custom-checkbox mr-4">
                  <input type="checkbox" id="radiusEnabled" name="radiusEnabled" className="custom-control-input" checked={form.radiusEnabled} onChange={onChange} />
                  <label className="custom-control-label" htmlFor="radiusEnabled">Use RADIUS</label>
                </div>
                <div className="custom-control custom-checkbox mr-4">
                  <input type="checkbox" id="portalVisible" name="portalVisible" className="custom-control-input" checked={form.portalVisible} onChange={onChange} />
                  <label className="custom-control-label" htmlFor="portalVisible">Show to customers</label>
                </div>
                <div className="custom-control custom-checkbox">
                  <input type="checkbox" id="active" name="active" className="custom-control-input" checked={form.active} onChange={onChange} />
                  <label className="custom-control-label" htmlFor="active">Active</label>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <input id="notes" name="notes" className="form-control" value={form.notes} onChange={onChange} placeholder="Mounting details, power, VLAN, etc." />
              </div>

              <div className="d-flex">
                <button type="submit" className="btn btn-primary mr-2" disabled={saving}>
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                      Saving…
                    </>
                  ) : editingId ? 'Save Changes' : 'Register Router'}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={resetForm} disabled={saving}>
                  Reset
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Side tips */}
      <div className="col-lg-4">
        <div className="card mb-3">
          <div className="card-body">
            <h6 className="card-title mb-2">Provisioning notes</h6>
            <ul className="mb-0 pl-3">
              <li><strong>MikroTik</strong>: paste the one-liner or download <code>.rsc</code> and paste in terminal.</li>
              <li><strong>Cisco / Ubiquiti / TP-Link</strong>: download vendor config and apply via CLI/controller.</li>
              <li>Ensure NTP/time sync to avoid auth issues.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
